import {
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';

import { useEffect, useState } from 'react';

import SearchIcon from '@mui/icons-material/Search';

import { AiTwotoneEdit } from 'react-icons/ai';
import moment from 'moment';
import { Container } from './FollowupPointControl.styles';
import IPage from '../../../interfaces/IPage';
import { useCalculationDate } from '../../../hooks/services/useCalculationDate';
import ICalculationDate from '../../../interfaces/ICalculationDate';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../../../components/page-title/PageTitle';
import FollowupSearchEmployee from '../../followup/FollowupSearchEmployee';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IEmployee from '../../../interfaces/IEmployee';
import { usePointControlService } from '../../../hooks/services/usePointControlService';

interface ITimeRecord {
  id: number;
  code: string;
  name: string;
  workstation: string;
  count: number;
}

interface IFilter {
  employeeId?: number | null;
}

interface FollowupPointControlProps extends IPage {}

const FollowupPointControl = (props: FollowupPointControlProps) => {
  const [timeRecords, setTimeRecords] = useState<ITimeRecord[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateSelected, setDateSelected] = useState('');
  const [calculationDates, setCalculationDates] = useState<ICalculationDate[]>();
  const [employee, setEmployee] = useState<IEmployee | null>(null);

  const request = usePointControlService();
  const requestCalculationDate = useCalculationDate();
  const toastr = useToastrVanila();
  const navigate = useNavigate();

  moment.locale();

  const handleChangeDate = async (event: SelectChangeEvent) => {
    setDateSelected(event.target.value);
    list(event.target.value);
  };

  const listPointControlCalculationDate = async (employeeId: number) => {
    setLoading(true);

    await requestCalculationDate
      .listCalculationDate(employeeId)
      .then(result => {
        setCalculationDates(result);
        setLoading(false);
        setDateSelected(`${result[0]?.initialDate} ${result[0]?.finalDate}`);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const list = async (date: string, filters?: IFilter) => {
    setLoading(true);
    const newDate = date.split(' ');

    const employeeId = filters?.employeeId ?? 0;
    await request
      .listByDateTimeRecords(newDate[0], newDate[1], {
        employeeId,
      })
      .then(async results => {
        setTimeRecords(results);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (dateSelected && employee?.id && dateSelected !== 'undefined undefined') {
      list(dateSelected ?? '0001-01-01 0001-01-01', {
        employeeId: employee?.id ?? 0,
      });
    }
  }, [dateSelected]);

  return (
    <Container>
      <PageTitle title={`FOLLOWUP DE PONTO`}></PageTitle>

      <Box sx={{ marginTop: 2 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
            <Typography>Filtros</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item md={3} sm={12} xs={12}>
                <FollowupSearchEmployee
                  setEmployee={employee => {
                    setEmployee(employee);

                    if (employee && employee.id !== undefined) {
                      listPointControlCalculationDate(employee?.id);
                    }
                  }}
                  employee={employee}
                />
              </Grid>
              <Grid item md={1} sm={12} xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="select-dates">Data de calculo</InputLabel>
                  <Select
                    labelId="select-dates"
                    label="Data de calculo"
                    onChange={handleChangeDate}
                    value={dateSelected}
                    sx={{ width: '260px', height: '40px' }}
                  >
                    {calculationDates?.map(date => (
                      <MenuItem key={date.id} value={`${date?.initialDate} ${date?.finalDate}`}>
                        {`${moment(date?.initialDate).format('DD/MM/yyyy')} até ${moment(date?.finalDate).format(
                          'DD/MM/yyyy',
                        )}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>

      <TableContainer component={Paper} sx={{ marginTop: '20px' }}>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Matrícula</TableCell>
              <TableCell>Nome do colaborador</TableCell>
              <TableCell>Posto de Trabalho</TableCell>
              <TableCell>Marcações Digitadas</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {timeRecords && timeRecords?.length !== 0 ? (
              timeRecords !== null &&
              timeRecords.map(timeRecord => {
                return (
                  <TableRow key={timeRecord?.code}>
                    <TableCell>{timeRecord.code}</TableCell>
                    <TableCell>{timeRecord?.name}</TableCell>
                    <TableCell>{timeRecord?.workstation}</TableCell>
                    <TableCell>{timeRecord?.count}</TableCell>
                    <TableCell>
                      <Tooltip title="Editar" placement="top">
                        <IconButton
                          onClick={async () => {
                            navigate(`/point-control-list/${dateSelected}/${timeRecord.id}`);
                            setLoading(false);
                          }}
                        >
                          <AiTwotoneEdit style={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ padding: 1, maxWidth: '100vw' }}
                  >
                    <SearchIcon sx={{ marginRight: 2, fontSize: '1.5rem' }} />
                    <Box sx={{ fontSize: '1.5rem' }}>Nenhum registro encontrado</Box>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default FollowupPointControl;
